/**
 * Unified WorkorderCard Component
 *
 * A single card component that supports both editable and readonly modes.
 * Used in day planning (editable with drag-and-drop) and my-day (readonly) panels.
 *
 * @example
 * // Editable mode (with drag-and-drop)
 * <WorkorderCard
 *   mode="editable"
 *   workorder={wo}
 *   index={idx}
 *   onArriveTimeChange={(time) => handleArriveTimeChange(wo, time)}
 *   technicianColor={color}
 *   routeError={routeErrors.get(wo.recnum)}
 * />
 *
 * @example
 * // Readonly mode
 * <WorkorderCard
 *   mode="readonly"
 *   workorder={wo}
 *   index={idx}
 *   routeError={routeErrors.get(wo.recnum)}
 * />
 */

// TODO: migrate to @dnd-kit/react (new API with type/accept/group constraints)
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  GripVertical,
  Navigation,
  Phone,
  AlertCircle,
  MapPinOff,
  Check,
  Clock,
} from 'lucide-react';
import { Workorder } from '@certhon/models';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { firstLine } from '@/utils/string';
import { useStackNavigate } from '@/app/navigation/stack-links';
import { WorkorderRouteError } from '../../dayplanning/dayplanning.hooks';
import { hasValidAddress } from '../utils/addressUtils';
import {
  TIME_SLOT_START_HOUR,
  TIME_SLOT_INTERVAL_MINUTES,
  TIME_SLOT_COUNT,
  TIME_CONVERSION,
} from '../constants';

// Generate time slots from start to end hour with configured interval
const TIME_SLOTS = Array.from({ length: TIME_SLOT_COUNT }, (_, i) => {
  const totalMinutes =
    TIME_SLOT_START_HOUR * TIME_CONVERSION.minutesPerHour +
    i * TIME_SLOT_INTERVAL_MINUTES;
  const hours = Math.floor(totalMinutes / TIME_CONVERSION.minutesPerHour);
  const minutes = totalMinutes % TIME_CONVERSION.minutesPerHour;
  const value = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  return { value, label: value };
});

/**
 * Base props shared by both modes
 */
interface WorkorderCardBaseProps {
  workorder: Workorder;
  /** 0-based index (displayed as 1-based) */
  index: number;
  /** Route error information (incoming/outgoing errors) */
  routeError?: WorkorderRouteError;
  /**
   * Path of the panel that renders this card. When given, clicking the card
   * drills down to `${linkBase}/workorders/:id` so the list stays visible;
   * otherwise it opens the global `/workorders/:id` branch.
   */
  linkBase?: string;
}

/**
 * Props for editable mode (drag-and-drop, time selection)
 */
interface EditableWorkorderCardProps extends WorkorderCardBaseProps {
  mode: 'editable';
  /** Callback when arrive time changes */
  onArriveTimeChange: (time: string | null) => void;
  /** Background color for the index badge (technician color) */
  technicianColor?: string;
}

/**
 * Props for readonly mode (display only)
 */
interface ReadonlyWorkorderCardProps extends WorkorderCardBaseProps {
  mode: 'readonly';
  onArriveTimeChange?: never;
  technicianColor?: never;
}

export type WorkorderCardProps =
  | EditableWorkorderCardProps
  | ReadonlyWorkorderCardProps;

/**
 * Completion badge overlay for the index badge
 */
function CompletionBadge({ dateCompleted }: { dateCompleted: Date | null }) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className="ring-card absolute -top-0.5 -right-0.5 flex h-3.5 w-3.5 items-center
              justify-center rounded-full bg-emerald-500 ring-2 sm:h-3 sm:w-3
              dark:bg-emerald-400"
            aria-label="Completed"
          >
            <Check
              className="h-2.5 w-2.5 text-white sm:h-2 sm:w-2"
              strokeWidth={3}
              aria-hidden="true"
            />
          </div>
        </TooltipTrigger>
        {dateCompleted && (
          <TooltipContent side="right">
            <p className="text-xs">
              Completed{' '}
              {dateCompleted.toLocaleTimeString('nl-NL', {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </p>
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Route error indicator with tooltip
 */
function RouteErrorIndicator({
  routeError,
  errorId,
}: {
  routeError: WorkorderRouteError;
  errorId: string;
}) {
  const errorMessage = [
    routeError.incomingError,
    routeError.outgoingError && `Return: ${routeError.outgoingError}`,
  ]
    .filter(Boolean)
    .join('. ');

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center">
            <MapPinOff
              className="h-4 w-4 shrink-0 text-red-600 dark:text-red-500"
              aria-hidden="true"
            />
            <span id={errorId} className="sr-only">
              Route calculation failed: {errorMessage}
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <p className="font-medium">Route calculation failed</p>
          {routeError.incomingError && (
            <p className="text-xs opacity-90">{routeError.incomingError}</p>
          )}
          {routeError.outgoingError && (
            <p className="mt-1 text-xs opacity-90">
              Return: {routeError.outgoingError}
            </p>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Missing address warning indicator
 */
function MissingAddressIndicator() {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center">
            <AlertCircle
              className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-500"
              aria-hidden="true"
            />
            <span className="sr-only">
              Missing address - excluded from route calculation
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>Missing address - excluded from route calculation</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Planned arrive time badge (readonly mode)
 */
function ArriveTimeBadge({ time }: { time: string }) {
  return (
    <div
      className="flex shrink-0 items-center gap-1 rounded-md bg-blue-100 px-2 py-0.5 text-xs
        font-semibold text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
    >
      <Clock className="h-3 w-3" aria-hidden="true" />
      <span aria-label={`Planned arrival time ${time}`}>{time}</span>
    </div>
  );
}

/**
 * Arrive time selector (editable mode)
 */
function ArriveTimeSelector({
  value,
  onChange,
  workorderId,
}: {
  value: string | null;
  onChange: (time: string | null) => void;
  workorderId: number;
}) {
  const selectId = `arrive-time-${workorderId}`;

  return (
    <div className="flex items-center gap-2">
      <label htmlFor={selectId} className="text-muted-foreground text-xs">
        Arrive:
      </label>
      <Select
        value={value?.slice(0, 5) || ''}
        onValueChange={(val) => onChange(val || null)}
      >
        <SelectTrigger
          id={selectId}
          className="h-6 text-xs"
          aria-label="Select arrival time"
        >
          <SelectValue placeholder="--:--" />
        </SelectTrigger>
        <SelectContent>
          {TIME_SLOTS.map((slot) => (
            <SelectItem key={slot.value} value={slot.value}>
              {slot.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/**
 * Action buttons for navigation and calling
 */
function ActionButtons({
  googleMapsUrl,
  phoneNumber,
  isReadonly,
}: {
  googleMapsUrl: string | null;
  phoneNumber: string | null;
  isReadonly: boolean;
}) {
  // Readonly mode has larger touch targets
  const buttonClass = isReadonly ? 'h-9 w-9 sm:h-7 sm:w-7' : 'h-7 w-7';
  const iconClass = isReadonly ? 'h-4 w-4 sm:h-3.5 sm:w-3.5' : 'h-3.5 w-3.5';
  const containerClass = isReadonly ? 'flex' : 'xs:flex hidden';

  return (
    <div className={`${containerClass} shrink-0 gap-1`}>
      {googleMapsUrl && (
        <Button
          size="icon"
          variant="ghost"
          className={buttonClass}
          asChild
          tooltip={false}
        >
          <a
            href={googleMapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Open navigation in Google Maps"
          >
            <Navigation className={iconClass} aria-hidden="true" />
          </a>
        </Button>
      )}
      {phoneNumber && (
        <Button
          size="icon"
          variant="ghost"
          className={buttonClass}
          asChild
          tooltip={false}
        >
          <a href={`tel:${phoneNumber}`} aria-label={`Call ${phoneNumber}`}>
            <Phone className={iconClass} aria-hidden="true" />
          </a>
        </Button>
      )}
    </div>
  );
}

export function WorkorderCard(props: WorkorderCardProps) {
  const { mode, workorder, index, routeError, linkBase = '' } = props;
  const isEditable = mode === 'editable';
  const navigate = useStackNavigate();
  const detailsPath = `${linkBase}/workorders/${workorder.recnum}`;

  // dnd-kit sortable - only active in editable mode
  const sortable = useSortable({
    id: workorder.recnum,
    disabled: !isEditable,
  });

  const handleCardClick = (event: React.MouseEvent) => {
    // Don't navigate if clicking on interactive elements
    const target = event.target as HTMLElement;
    const interactiveSelector = isEditable
      ? 'button, a, input, select, textarea'
      : 'button, a';
    if (target.closest(interactiveSelector)) {
      return;
    }
    navigate({ to: detailsPath });
  };

  const handleCardKeyDown = (event: React.KeyboardEvent) => {
    // Handle Enter and Space for card navigation
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      navigate({ to: detailsPath });
    }
  };

  const googleMapsUrl = workorder.location_address
    ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(workorder.location_address)}`
    : null;

  const phoneNumber = workorder.contact_phone || workorder.location_phone;
  const hasAddress = hasValidAddress(workorder);
  const hasRouteError = !!(
    routeError?.incomingError || routeError?.outgoingError
  );

  // Style based on mode
  const style = isEditable
    ? {
        transform: CSS.Transform.toString(sortable.transform),
        transition: sortable.transition,
        opacity: sortable.isDragging ? 0.5 : 1,
      }
    : undefined;

  // Build card class
  const cardClasses = [
    'cursor-pointer gap-0 py-0',
    workorder.completed ? 'opacity-70' : '',
    hasRouteError
      ? 'border-red-500/50'
      : !hasAddress
        ? 'border-amber-500/50'
        : '',
    // Readonly mode additions
    !isEditable ? 'hover:bg-accent/50 transition-colors' : '',
    // Editable mode additions
    isEditable ? 'touch-pan-y' : '',
  ]
    .filter(Boolean)
    .join(' ');

  // Index badge styling
  const badgeStyle =
    isEditable && props.technicianColor
      ? { backgroundColor: props.technicianColor }
      : undefined;
  const badgeClasses = isEditable
    ? 'flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white'
    : 'bg-primary text-primary-foreground flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-medium sm:h-6 sm:w-6 sm:text-xs';

  // Display index (1-based)
  const displayIndex = index + 1;

  // Generate unique IDs for ARIA relationships
  const errorId = `error-${workorder.recnum}`;

  // Create accessible label for the card
  const cardLabel = [
    `Workorder ${displayIndex}`,
    `W${workorder.id}`,
    workorder.customer_name,
    workorder.work_name,
    workorder.completed && 'completed',
    hasRouteError && 'route error',
    !hasAddress && 'missing address',
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <Card
      ref={isEditable ? sortable.setNodeRef : undefined}
      style={style}
      className={cardClasses}
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
      role="button"
      tabIndex={0}
      aria-label={cardLabel}
      aria-describedby={hasRouteError ? errorId : undefined}
    >
      {/* Main content row */}
      <div
        className={`flex items-center gap-${isEditable ? '2' : '3'} px-3
          py-${isEditable ? '2' : '3'} sm:py-2`}
      >
        {/* Drag handle (editable only) */}
        {isEditable && (
          <button
            {...sortable.attributes}
            {...sortable.listeners}
            className="text-muted-foreground hover:text-foreground focus-visible:ring-ring -ml-1
              shrink-0 cursor-grab touch-none p-0.5 focus:outline-none focus-visible:ring-2
              focus-visible:ring-offset-2 active:cursor-grabbing"
            aria-label="Drag to reorder workorder"
            tabIndex={0}
          >
            <GripVertical className="h-4 w-4" aria-hidden="true" />
          </button>
        )}

        {/* Index badge with completion indicator */}
        <div className="relative">
          <span
            className={badgeClasses}
            style={badgeStyle}
            aria-label={`Position ${displayIndex}`}
          >
            {displayIndex}
          </span>
          {workorder.completed && (
            <CompletionBadge dateCompleted={workorder.date_completed} />
          )}
        </div>

        {/* Route error indicator */}
        {hasRouteError && (
          <RouteErrorIndicator routeError={routeError!} errorId={errorId} />
        )}

        {/* Warning icon for missing address (only show if no route error) */}
        {!hasAddress && !hasRouteError && <MissingAddressIndicator />}

        {/* Main content */}
        <div className="min-w-0 flex-1 overflow-hidden">
          <div className={`flex items-center gap-2 ${!isEditable ? '' : ''}`}>
            <div className="truncate text-sm font-medium">
              <span className="shrink-0">W{workorder.id}</span>
              {workorder.customer_name && (
                <span className="text-muted-foreground ml-1.5 font-normal">
                  {workorder.customer_name}
                </span>
              )}
              {/* Work name distinguishes bons that share one (meta) customer */}
              {workorder.work_name && (
                <span className="text-muted-foreground ml-1.5 font-normal">
                  · {workorder.work_name}
                </span>
              )}
            </div>
            {/* Arrive time badge (readonly mode only) */}
            {!isEditable && workorder.planned_arrive_time && (
              <ArriveTimeBadge time={workorder.planned_arrive_time} />
            )}
          </div>
          {workorder.description && (
            <div className="text-muted-foreground truncate text-xs">
              {firstLine(workorder.description)}
            </div>
          )}
        </div>

        {/* Action buttons */}
        <ActionButtons
          googleMapsUrl={googleMapsUrl}
          phoneNumber={phoneNumber}
          isReadonly={!isEditable}
        />
      </div>

      {/* Footer row */}
      <div
        className={`bg-muted/30 flex items-center gap-2 border-t px-3
          ${isEditable ? 'justify-between py-1.5' : 'py-2 text-xs sm:py-1.5'}`}
      >
        {isEditable && props.onArriveTimeChange ? (
          <>
            <ArriveTimeSelector
              value={workorder.planned_arrive_time}
              onChange={props.onArriveTimeChange}
              workorderId={workorder.recnum}
            />
            <span className="text-muted-foreground truncate text-xs">
              {workorder.location_address?.replace(/\n/g, ', ') || 'No address'}
            </span>
          </>
        ) : (
          <span className="text-muted-foreground min-w-0 flex-1 truncate">
            {workorder.location_address?.replace(/\n/g, ', ') || 'No address'}
          </span>
        )}
      </div>
    </Card>
  );
}
